import dagre from 'dagre';
import { Edge, Node, getOutgoers } from '@xyflow/react';
import { EmployeeDetail, OrgChartEmployee, OrgChartNode } from './types';
import i18next from 'i18next';
import { Theme } from '../../../../theme';

export const NODE_WIDTH = 145;
export const NODE_HEIGHT = 100;
const nodePosition = { x: 0, y: 0 };

export const getLayoutedElements = (
  nodes: OrgChartNode[],
  edges: Edge[],
  direction = 'TB',
) => {
  // A fresh graph per call — reusing a singleton retains stale nodes from
  // previous filter states and pushes the visible subgraph off-canvas.
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  // v12 requires new node objects — mutating `node.position` in place does not
  // trigger a re-render.
  const layoutedNodes = nodes.map((node) => {
    const { x, y } = dagreGraph.node(node.id);
    return {
      ...node,
      position: { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 },
    };
  });

  return { nodes: layoutedNodes, edges };
};

export const getNodesAndEdges = (
  employees: OrgChartEmployee[],
  selectedSettings: string[],
) => {
  const nodes: OrgChartNode[] = [];
  const edges: Edge[] = [];
  employees.forEach((e) => {
    nodes.push({
      id: e.id,
      position: nodePosition,
      type: 'employeeNode',
      data: { employee: e, selectedSettings },
    });

    if (e.manager?.id) {
      edges.push({
        id: `e${e.manager.id}-${e.id}`,
        source: e.manager.id,
        target: e.id,
        type: 'smoothstep',
        style: {
          stroke: Theme.color.paper1,
        },
      });
    }
  });

  return getLayoutedElements(nodes, edges);
};

const i18Path = 'people.orgChart';
export const getEmployeeDetailsLabel = (detail: EmployeeDetail) =>
  i18next.t(`${i18Path}.${detail}`);

// Walks the subtree below `node`. Salesforce does not stop a manager chain from
// looping back on itself (A → B → A, or someone set as their own manager), so
// the visited set is what keeps a bad hierarchy from becoming infinite
// recursion and hanging the tab.
export const getChildrenIds = (
  node: Node,
  nodes: Node[],
  edges: Edge[],
  childrenIds: string[],
  visited: Set<string> = new Set([node.id]),
): string[] => {
  const children = getOutgoers(node, nodes, edges).filter(
    (child) => !visited.has(child.id),
  );
  children.forEach((child) => visited.add(child.id));

  childrenIds = childrenIds.concat(children.map((o) => o.id));
  children.forEach((n) => {
    childrenIds = getChildrenIds(n, nodes, edges, childrenIds, visited);
  });
  return childrenIds;
};
